/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Form } from 'react-bootstrap';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { UzbekLatin } from 'flatpickr/dist/l10n/uz_latn.js';
import Flatpickr from 'react-flatpickr';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import legalEntityCurrencyService from 'service/legalEntityCurrency';
import MonthlyEmployeeTable from './monthlyEmployeeTable';
import customersService from 'service/customer';
import Button from 'components/base/Button';

export const installmentReportsBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'installment',
    url: '/installment'
  },

  {
    label: 'monthly_employee_report',
    active: true
  }
];

const MonthlyEmployeeReport = () => {
  const { t, i18n } = useTranslation();
  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_id
  );

  const [dateFrom, setDateFrom] = useState<any>(null);
  const [dateTo, setDateTo] = useState<any>(null);
  const [selectedCurrencyPid, setSelectedCurrencyPid] = useState<any>(0);
  const [currencyOption, setCurrencyOption] = useState<any>([]);
  const [isCurrencyInitialized, setIsCurrencyInitialized] = useState(false);
  const [selectedStatusPid, setSelectedStatusPid] = useState<any>('');
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [selectedCustomerPid, setSelectedCustomerPid] = useState<any>('');

  const handleStartDateChange = (date: Date[]) => {
    const selectedDate = date[0]; // Flatpickr returns an array, take the first date
    if (selectedDate) {
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0); // Set time to 00:00
      const startDateInMillis = startDate.getTime();
      setDateFrom(startDateInMillis);

      // Calculate end date: Last day of the 12th month from startDate
      const endDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + 12,
        0
      );
      endDate.setHours(23, 59, 59, 999); // Set time to end of day
      const endDateInMillis = endDate.getTime();
      setDateTo(endDateInMillis);
    }
  };

  const handleEndDateChange = (date: Date[]) => {
    const selectedDate = date[0];
    if (selectedDate) {
      const endDate = new Date(selectedDate);
      endDate.setHours(23, 59, 59, 999); // Set time to end of day
      const endDateInMillis = endDate.getTime();
      setDateTo(endDateInMillis);
    }
  };

  //GET LEgal Entity to options
  useQuery(['GET_LEGAL_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.pid,
          currency_id: option?.currency?.id,
          label: option?.currency?.name?.[i18n?.language]
        }));
        setCurrencyOption(options);
      });
  });

  //GET customer to options
  useQuery(['GET_CUSTOMER'], async () => {
    await customersService
      .getList({ offset: 0, limit: 1000 })
      .then((res: any) => {
        const options = res?.data?.users?.map((options: any) => ({
          value: options.id,
          label: options.first_name + ' ' + options?.last_name
        }));
        setCustomerOptions(options);
      });
  });

  const statusOption = [
    {
      label: t('paid'),
      value: 'PAID'
    },
    {
      label: t('overdue'),
      value: 'EXPIRED'
    },
    {
      label: t('coming'),
      value: 'COMING'
    },
    {
      label: t('partially_paid'),
      value: 'PARTIALLY_PAID'
    }
  ];

  const getFlatpickrLocale = () => {
    switch (i18n.language) {
      case 'ru':
        return Russian;
      case 'uz':
        return UzbekLatin;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (currencyId && currencyOption.length > 0 && !isCurrencyInitialized) {
      const defaultCurrency = currencyOption.find(
        (option: any) => option.currency_id === currencyId
      );
      if (defaultCurrency) {
        setSelectedCurrencyPid(defaultCurrency.value);
        setIsCurrencyInitialized(true);
      }
    }
  }, [currencyId, currencyOption, isCurrencyInitialized]);

  useEffect(() => {
    const today = new Date();

    const dateFrom = new Date(today.getFullYear(), today.getMonth(), 1);

    const dateTo = new Date(today.getFullYear(), today.getMonth() + 12, 0);

    setDateFrom(dateFrom.getTime());
    setDateTo(dateTo.getTime());
  }, []);

  return (
    <div>
      <PageBreadcrumb items={installmentReportsBreadcrumbItems} />

      <h2 className="mb-5">{t('monthly_employee_report')}</h2>

      <div className="d-flex gap-2 mb-3">
        <div className="d-flex gap-2">
          <Form.Group className="md-4">
            <Form.Floating>
              <Flatpickr
                className={classNames('form-control')}
                options={{
                  nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                  prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                  locale: getFlatpickrLocale(),
                  monthSelectorType: 'static',
                  minDate: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 12,
                    new Date().getDate()
                  ), // Exactly 12 months back
                  maxDate: new Date(
                    new Date().getFullYear() + 1,
                    new Date().getMonth(),
                    0
                  ),
                  onDayCreate: (...[, , , dayElem]) => {
                    if (
                      dayElem.dateObj.getDay() === 5 ||
                      dayElem.dateObj.getDay() === 6
                    ) {
                      dayElem.className += ' weekend-days';
                    }
                  },
                  dateFormat: 'M j, Y',
                  disableMobile: true
                }}
                value={dateFrom}
                onChange={handleStartDateChange}
                placeholder={t('date_from')}
              />
              <label htmlFor="floatingInputCustom">{t('date_from')}</label>
            </Form.Floating>
          </Form.Group>

          <Form.Group className="md-4">
            <Form.Floating>
              <Flatpickr
                className={classNames('form-control')}
                options={{
                  nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
                  prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
                  locale: getFlatpickrLocale(),
                  monthSelectorType: 'static',
                  minDate: dateFrom
                    ? new Date(dateFrom)
                    : new Date(
                        new Date().getFullYear(),
                        new Date().getMonth() - 1,
                        1
                      ),
                  maxDate: dateFrom
                    ? new Date(
                        new Date(dateFrom).getFullYear(),
                        new Date(dateFrom).getMonth() + 12,
                        0
                      )
                    : new Date(
                        new Date().getFullYear() + 1,
                        new Date().getMonth(),
                        0
                      ),
                  onDayCreate: (...[, , , dayElem]) => {
                    if (
                      dayElem.dateObj.getDay() === 5 ||
                      dayElem.dateObj.getDay() === 6
                    ) {
                      dayElem.className += ' weekend-days';
                    }
                  },
                  dateFormat: 'M j, Y',
                  disableMobile: true
                }}
                value={dateTo}
                onChange={handleEndDateChange}
                placeholder={t('date_to')}
              />
              <label htmlFor="floatingInputCustom">{t('date_to')}</label>
            </Form.Floating>
          </Form.Group>
        </div>
        <Form.Group className="md-4">
          <div className="react-select-container">
            <Form.Floating>
              <Form.Select
                value={selectedCurrencyPid ?? ''}
                onChange={e => setSelectedCurrencyPid(e.target.value || null)}
              >
                <option value="" disabled hidden></option>
                {currencyOption?.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">{t('currency')}</label>
            </Form.Floating>
          </div>
        </Form.Group>
        <Form.Group className="md-4">
          <div className="react-select-container">
            <Form.Floating>
              <Form.Select
                value={selectedCustomerPid ?? ''}
                onChange={e => setSelectedCustomerPid(e.target.value || '')}
              >
                <option value="" disabled hidden></option>
                {customerOptions?.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">{t('users')}</label>

              {selectedCustomerPid !== '' && (
                <Button
                  variant="link"
                  style={{
                    position: 'absolute',
                    right: '5%',
                    top: '15%',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSelectedCustomerPid('');
                  }}
                >
                  <FeatherIcon icon="delete" size={16} />
                </Button>
              )}
            </Form.Floating>
          </div>
        </Form.Group>
        <Form.Group className="md-4">
          <div className="react-select-container">
            <Form.Floating>
              <Form.Select
                value={selectedStatusPid || ''}
                onChange={e => setSelectedStatusPid(e.target.value || null)}
              >
                <option value="" disabled hidden></option>
                {statusOption?.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">{t('status')}</label>
              {selectedStatusPid && (
                <Button
                  variant="link"
                  style={{
                    position: 'absolute',
                    right: '5%',
                    top: '15%',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSelectedStatusPid(null);
                  }}
                >
                  <FeatherIcon icon="delete" size={16} />
                </Button>
              )}
            </Form.Floating>
          </div>
        </Form.Group>
      </div>

      <MonthlyEmployeeTable
        selectedCurrencyPid={selectedCurrencyPid}
        dateFrom={dateFrom}
        dateTo={dateTo}
        selectedStatusPid={selectedStatusPid}
        selectedCustomerPid={selectedCustomerPid}
      />
    </div>
  );
};

export default MonthlyEmployeeReport;
